import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const PaymentHistoryPage: React.FC = () => {
  useDocumentTitle('Payment History');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPayments();
  }, [page]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await subscriptionAPI.listPayments(page, 20);
      const result = response.data;
      setPayments(result.data || []);
      setTotalPages(result.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      succeeded: 'bg-success', pending: 'bg-warning', processing: 'bg-info',
      failed: 'bg-danger', refunded: 'bg-secondary', partially_refunded: 'bg-warning',
    };
    return <span className={`badge ${colors[status] || 'bg-secondary'}`}>{status.replace('_', ' ')}</span>;
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="mb-4">
          <button className="btn btn-link text-decoration-none p-0 mb-2" onClick={() => navigate('/billing')}>
            <i className="bi bi-arrow-left me-2"></i>Back to Billing
          </button>
          <h2 className="h3 fw-bold">Payment History</h2>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <div className="d-flex justify-content-center py-5"><LoadingSpinner /></div>
            ) : payments.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-receipt" style={{ fontSize: '3rem' }}></i>
                <p className="mt-3">No payments found</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Gateway</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p: any) => (
                      <tr key={p.id}>
                        <td>{new Date(p.paid_at || p.created_at).toLocaleDateString()}</td>
                        <td>{p.plan_name || 'Subscription'} Plan</td>
                        <td className="text-capitalize">{p.payment_type}</td>
                        <td className="text-capitalize">{p.gateway}</td>
                        <td className="fw-medium">{p.currency} {(parseFloat(p.amount) || 0).toFixed(2)}</td>
                        <td>{getStatusBadge(p.status)}</td>
                        <td>
                          {p.status === 'succeeded' && p.receipt_number && (
                            <button className="btn btn-link btn-sm p-0" onClick={() => navigate(`/billing/receipt/${p.id}`)}>
                              <i className="bi bi-receipt me-1"></i>{p.receipt_number}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="card-footer bg-white d-flex justify-content-center">
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => p - 1)}>Previous</button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => p + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaymentHistoryPage;
